import { useMemo } from 'react';

import { getPackageRoomTypeId } from '~/components/Offers/Detail/OfferBuilder/Content/OfferPackages/utils';
import { isAccommodationPackage } from '~/components/Offers/Detail/typeNarrowingUtils';

import { useGetProperty } from '~/hooks/apis/accommodation/useGetProperty';
import { useGetVendorOffers } from '~/hooks/apis/offers/useGetVendorOffers';

export interface LinkedOffer {
  id: string;
  name: string;
}

export interface OfferRoomType {
  id: string;
  name: string;
  roomTypeCode?: string | null;
  roomGroup?: string;
  linkedOffers: Array<LinkedOffer>;
  defaultRoomRateId?: string;
}

function getDefaultRoomRateId(roomType: App.RoomType): string | undefined {
  const defaultRoomRate =
    roomType.room_rates?.find((roomRate) => roomRate.rate_plan?.default_plan) ?? roomType.room_rates?.[0];
  return defaultRoomRate?.id;
}

function getPackageRoomTypeIds(offerPackage: App.Package, roomTypeIdByRoomRateId: Map<string, string>): Array<string> {
  const roomTypeIds = new Set<string>();
  const packageRoomTypeId = getPackageRoomTypeId(offerPackage);

  if (packageRoomTypeId) {
    roomTypeIds.add(packageRoomTypeId);
  }

  if (isAccommodationPackage(offerPackage)) {
    offerPackage.package_options?.forEach((option) => {
      const roomTypeId = option.fk_room_rate_id && roomTypeIdByRoomRateId.get(option.fk_room_rate_id);
      if (roomTypeId) {
        roomTypeIds.add(roomTypeId);
      }
    });
  }

  return Array.from(roomTypeIds);
}

export function useGetOfferRoomTypes(propertyId: string | undefined, vendorId: string | undefined) {
  const { data: property, isLoading: isLoadingProperty, error: propertyError } = useGetProperty({ propertyId });
  const { data: offers = [], isLoading: isLoadingOffers, error: offersError } = useGetVendorOffers({ vendorId });

  const roomTypes = useMemo<Array<OfferRoomType>>(() => {
    const roomTypeIdByRoomRateId = new Map<string, string>();
    (property?.room_types ?? []).forEach((roomType) => {
      (roomType.room_rates ?? []).forEach((roomRate) => roomTypeIdByRoomRateId.set(roomRate.id, roomType.id));
    });

    const roomTypeOffers = new Map<string, Array<LinkedOffer>>();

    offers.forEach((offer) => {
      const linkedOffer = { id: offer.id_salesforce_external, name: offer.name };
      (offer.packages ?? []).forEach((offerPackage) => {
        getPackageRoomTypeIds(offerPackage, roomTypeIdByRoomRateId).forEach((roomTypeId) => {
          const existing = roomTypeOffers.get(roomTypeId) ?? [];
          if (!existing.some((linked) => linked.id === linkedOffer.id)) {
            roomTypeOffers.set(roomTypeId, existing.concat([linkedOffer]));
          }
        });
      });
    });

    return (property?.room_types ?? []).map((roomType) => ({
      id: roomType.id,
      name: roomType.name,
      roomTypeCode: roomType.room_type_code,
      roomGroup: roomType.room_types_group?.name,
      linkedOffers: roomTypeOffers.get(roomType.id) ?? [],
      defaultRoomRateId: getDefaultRoomRateId(roomType),
    }));
  }, [property?.room_types, offers]);

  return {
    data: roomTypes,
    isLoading: isLoadingProperty || isLoadingOffers,
    error: propertyError || offersError,
  };
}
